import React from 'react';
import { Building2 } from 'lucide-react';

interface SimpleSociety {
  uuid: string;
  name: string;
  code?: string;
  city?: string;
  parentId?: string | null;
}

interface ChildSocietiesMultiSelectProps {
  societies: SimpleSociety[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

export const ChildSocietiesMultiSelect: React.FC<ChildSocietiesMultiSelectProps> = ({
  societies,
  selectedIds,
  onChange,
  disabled = false,
}) => {
  const handleToggle = (societyId: string) => {
    if (disabled) return;

    if (selectedIds.includes(societyId)) {
      onChange(selectedIds.filter(id => id !== societyId));
    } else {
      onChange([...selectedIds, societyId]);
    }
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange(societies.map(s => s.uuid));
  };

  const handleDeselectAll = () => {
    if (disabled) return;
    onChange([]);
  };

  if (societies.length === 0) {
    return (
      <div className="tw-mt-4 tw-p-3 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-text-sm tw-text-gray-500">
        Cette société n'a pas de sociétés enfants disponibles.
      </div>
    );
  }

  const allSelected = selectedIds.length === societies.length;

  return (
    <div className="tw-mt-2">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700">
          Autres sociétés (optionnel)
        </label>
        <div className="tw-flex tw-gap-2">
          <button
            type="button"
            onClick={handleSelectAll}
            disabled={disabled || allSelected}
            className="tw-text-xs tw-text-blue-600 hover:tw-text-blue-800 disabled:tw-opacity-50"
          >
            Tout sélectionner
          </button>
          <button
            type="button"
            onClick={handleDeselectAll}
            disabled={disabled || selectedIds.length === 0}
            className="tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 disabled:tw-opacity-50"
          >
            Tout désélectionner
          </button>
        </div>
      </div>

      <p className="tw-text-xs tw-text-gray-500 tw-mb-2">
        Sélectionnez les sociétés du groupe auxquelles cet utilisateur aura accès
      </p>

      <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-divide-y tw-divide-gray-100 tw-max-h-48 tw-overflow-y-auto">
        {societies.map((society) => {
          const isSelected = selectedIds.includes(society.uuid);

          return (
            <label
              key={society.uuid}
              className={`tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1.5 tw-cursor-pointer hover:tw-bg-gray-50 tw-transition-colors ${
                disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleToggle(society.uuid)}
                disabled={disabled}
                className="tw-w-3.5 tw-h-3.5 tw-text-blue-600 tw-border-gray-300 tw-rounded focus:tw-ring-blue-500"
              />

              <Building2 className="tw-w-3.5 tw-h-3.5 tw-text-gray-400 tw-flex-shrink-0" />

              <span className="tw-flex-1 tw-min-w-0 tw-text-sm tw-font-medium tw-text-gray-900 tw-truncate">
                {society.name}
              </span>
              {(society.code || society.city) && (
                <span className="tw-text-xs tw-text-gray-400 tw-flex-shrink-0">
                  {society.code}{society.code && society.city ? ' \u2022 ' : ''}{society.city}
                </span>
              )}
            </label>
          );
        })}
      </div>

      {selectedIds.length > 0 && (
        <p className="tw-mt-1.5 tw-text-xs tw-text-gray-600">
          {selectedIds.length} société{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
};
